import {
  ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from '@shared/components/ui/chart';
import { cn } from '@shared/lib/utils';
import { ClassValue } from 'clsx';
import { ComponentType } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  XAxis,
  XAxisProps,
  YAxis,
  YAxisProps,
} from 'recharts';
import { DataKey, Margin } from 'recharts/types/util/types';
import { CustomTooltipProps } from './tooltips/types';

interface DualAxisBarChartProps<T extends string, U extends string> {
  nameKey: T;
  dataKey: U;
  leftAxisConfig: ChartConfig;
  rightAxisConfig?: ChartConfig;
  chartData: any;
  legend?: boolean;
  className?: ClassValue;
  layout?: 'horizontal' | 'vertical';
  margin?: Margin;
  LeftYAxisProps?: YAxisProps;
  RightYAxisProps?: YAxisProps;
  XAxisProps?: XAxisProps;
  MaxBarSize?: number;
  barCategoryGap?: number | string;
  barSize?: number;
  stacked?: boolean;
  tooltipFormatter?: ComponentType<CustomTooltipProps>;
}

export default function DualAxisBarChart<
  T extends DataKey<string>,
  U extends DataKey<string[]>,
>({
  nameKey,
  dataKey,
  chartData,
  legend,
  className,
  layout = 'horizontal',
  margin = {
    top: 10,
    right: 10,
    bottom: 10,
    left: 10,
  },
  LeftYAxisProps,
  RightYAxisProps,
  XAxisProps,
  MaxBarSize = 100,
  barCategoryGap = '10%',
  barSize,
  tooltipFormatter: Component,
  leftAxisConfig,
  rightAxisConfig,
  stacked = true,
}: DualAxisBarChartProps<T, U>) {
  const allConfigs = { ...leftAxisConfig, ...rightAxisConfig };
  const chartConfig = Object.keys(allConfigs).reduce((acc, key) => {
    const config = allConfigs[key];
    acc[key] = {
      label: config.label,
      color: config.color,
      unit: config.unit,
    };
    return acc;
  }, {} as ChartConfig);
  return (
    <ChartContainer config={chartConfig} className={cn('', className)}>
      <BarChart
        accessibilityLayer
        data={chartData}
        layout={layout}
        margin={margin}
        barCategoryGap={barCategoryGap}
      >
        <CartesianGrid vertical={false} stroke="var(--border)" />
        {layout === 'horizontal' && (
          <>
            <XAxis
              dataKey={nameKey}
              tickLine={false}
              tickMargin={10}
              type="category"
              {...XAxisProps}
            />
            <YAxis
              yAxisId="left"
              tickLine={false}
              tickMargin={10}
              tickFormatter={(value) => value}
              {...LeftYAxisProps}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              tickLine={false}
              tickMargin={10}
              tickFormatter={(value) => value}
              {...RightYAxisProps}
            />
          </>
        )}

        {layout === 'vertical' && (
          <>
            <XAxis
              type="number"
              dataKey={dataKey}
              orientation="top"
              {...XAxisProps}
            />
            <YAxis
              dataKey={nameKey}
              type="category"
              tickLine={false}
              tickMargin={10}
              {...LeftYAxisProps}
            />
          </>
        )}
        <ChartTooltip
          content={
            <ChartTooltipContent
              className={cn('w-60', {
                'w-fit': Component,
              })}
              formatter={
                Component
                  ? (value, name, item, index) => (
                      <Component
                        value={value}
                        name={name}
                        item={item}
                        index={index}
                        chartConfig={chartConfig}
                      />
                    )
                  : undefined
              }
            />
          }
        />
        {legend && (
          <ChartLegend
            content={<ChartLegendContent className="text-md" />}
            verticalAlign="top"
          />
        )}
        {Object.keys(leftAxisConfig).map((key) => {
          return (
            <Bar
              dataKey={key}
              key={key}
              yAxisId="left"
              stackId={stacked ? 'a' : undefined}
              fill={chartConfig[key].color}
              radius={[0, 0, 0, 0]}
              maxBarSize={MaxBarSize}
              isAnimationActive={false}
              barSize={barSize}
            />
          );
        })}

        {rightAxisConfig &&
          Object.keys(rightAxisConfig).map((key) => {
            return (
              <Bar
                dataKey={key}
                key={key}
                yAxisId="right"
                stackId={stacked ? 'b' : undefined}
                fill={chartConfig[key].color}
                radius={[0, 0, 0, 0]}
                maxBarSize={MaxBarSize}
                isAnimationActive={false}
                barSize={barSize}
              />
            );
          })}
      </BarChart>
    </ChartContainer>
  );
}
